// LeaderboardCategoryUsers.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  InfiniteScroller,
  ScrollItem,
} from '../component/InfiniteScroller.jsx';
import { useInView } from 'react-intersection-observer';
import HydrationBoundary from '../component/HydrationBoundary.jsx';
import LeaderboardCategoryUser from '../container/LeaderboardCategoryUser.js';
import { RANGE_24H, RANGES } from '../resource/leaderboardConstants.js';
import { getIsInBrowserMainThread } from '../resource/getJsEnvironment.js';

import loadingIcon from '../../img/img-loading-grey-20.svg';

const isClient = getIsInBrowserMainThread();

const UsersPlaceholder = Array(10)
  .fill(null)
  .map((userId, index) => {
    const place = index + 1;
    return (
      <LeaderboardCategoryUser key={index} userId={userId} place={place} />
    );
  });

export class LeaderboardCategoryUsers extends React.PureComponent {
  nextTick = null;
  componentDidMount() {
    this.nextTick = setTimeout(() => {
      const { isActive, fetchLeaderboardUsers } = this.props;
      if (isActive) {
        fetchLeaderboardUsers({ page: 1 });
      }
    }, 0);
  }
  componentDidUpdate(prevProps) {
    const {
      isActive: prevIsActive,
      isAuthed: prevIsAuthed,
      range: prevRange,
      region: prevRegion,
    } = prevProps;
    const { range, region, isActive, isAuthed, fetchLeaderboardUsers } =
      this.props;
    const shouldFetchData =
      isActive &&
      (isActive !== prevIsActive ||
        range !== prevRange ||
        isAuthed !== prevIsAuthed ||
        region !== prevRegion);

    if (shouldFetchData) {
      this.nextTick = setTimeout(() => {
        fetchLeaderboardUsers({ page: 1 });
      }, 0);
    }
  }
  componentWillUnmount() {
    clearTimeout(this.nextTick);
    if (!window.__IS_HYDRATED__) {
      window.__IS_HYDRATED__ = 'LeaderboardCategoryUsers';
    }
  }
  render() {
    const {
      nextPage,
      isNextPageFetching,
      isFetched,
      isActive,
      fetchLeaderboardUsers,
      region,
      range,
      category,
      userIds,
    } = this.props;
    const shouldShowPlaceholder = !isFetched && userIds.length === 0;
    const hasMore = nextPage != null;
    const needLoadMore = isActive && hasMore;
    if (shouldShowPlaceholder) {
      return (
        <StyledLeaderboardCategoryUsers>
          <Users>{UsersPlaceholder}</Users>
        </StyledLeaderboardCategoryUsers>
      );
    }
    const shouldHydrate = !isClient || !window.__IS_HYDRATED__;
    return (
      <StyledLeaderboardCategoryUsers>
        <InfiniteScroller
          pageStart={1}
          threshold={50}
          hasMore={hasMore}
          isFetching={isNextPageFetching}
          loadMore={() =>
            needLoadMore ? fetchLeaderboardUsers({ page: nextPage }) : null
          }
        >
          <Users>
            <ScrollItem
              loader={
                <LoaderWrapper>
                  <Loader
                    src={loadingIcon}
                    width="32"
                    height="32"
                    alt="loading"
                  />
                </LoaderWrapper>
              }
            >
              {userIds.map((userId, index) => {
                const place = index + 1;

                return (
                  <ObservableLeaderboardUser
                    key={`${userId}-${place}`}
                    userId={userId}
                    place={place}
                    region={region}
                    range={range}
                    category={category}
                    shouldHydrate={shouldHydrate}
                  />
                );
              })}
            </ScrollItem>
          </Users>
        </InfiniteScroller>
      </StyledLeaderboardCategoryUsers>
    );
  }
}

LeaderboardCategoryUsers.propTypes = {
  fetchLeaderboardUsers: PropTypes.func,
  userIds: PropTypes.array,
  nextPage: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
  range: PropTypes.oneOf(RANGES),
  region: PropTypes.string,
  category: PropTypes.string,
  isNextPageFetching: PropTypes.bool,
  isFetched: PropTypes.bool,
  isActive: PropTypes.bool,
  isAuthed: PropTypes.bool,
};

LeaderboardCategoryUsers.defaultProps = {
  fetchLeaderboardUsers: () => null,
  userIds: [],
  nextPage: null,
  range: RANGE_24H,
  region: null,
  category: '',
  isNextPageFetching: true,
  isFetched: false,
  isActive: false,
  isAuthed: false,
};

const OBSERVER_OPTIONS = {
  threshold: 0.5,
  triggerOnce: true,
};

const ObservableLeaderboardUser = ({
  userId,
  place,
  region,
  range,
  category,
  shouldHydrate,
}) => {
  const { ref, inView } = useInView(OBSERVER_OPTIONS);

  if (shouldHydrate) {
    return (
      <HydrationBoundary
        shouldHydrate={inView}
        wrapper={<UserWrapper ref={ref} />}
      >
        <LeaderboardCategoryUser
          userId={userId}
          place={place}
          region={region}
          range={range}
          category={category}
        />
      </HydrationBoundary>
    );
  }

  if (inView) {
    return (
      <UserWrapper ref={ref}>
        <LeaderboardCategoryUser
          userId={userId}
          place={place}
          region={region}
          range={range}
          category={category}
        />
      </UserWrapper>
    );
  }

  return <UserWrapper ref={ref} />;
};

ObservableLeaderboardUser.propTypes = {
  userId: PropTypes.string,
  place: PropTypes.number,
  region: PropTypes.string,
  range: PropTypes.string,
  category: PropTypes.string,
  shouldHydrate: PropTypes.bool,
};

const StyledLeaderboardCategoryUsers = styled.div``;

const Users = styled.div`
  padding-top: 20px;
  overflow-y: hidden;
`;

const LoaderWrapper = styled.div`
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Loader = styled.img`
  opacity: 0.5;
`;

const UserWrapper = styled.div`
  padding: 0px 16px;
  margin-bottom: 20px;
`;
export default LeaderboardCategoryUsers;
